import React, { useCallback, useEffect, useState } from 'react';
import { sentenceCase } from 'change-case';
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  tableCellClasses,
  OutlinedInput,
  InputAdornment,
  Tooltip,
  Select,
  MenuItem
} from '@mui/material';
import { Box, styled } from '@mui/system';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format } from 'date-fns';

import { ReceiveApi } from '../../utils/Apis';
import Page from '../../components/Page';
import BrudCrumbs from '../../components/BreadCrumbs';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserMoreMenu } from '../../components/_dashboard/user';
import { fDate } from '../../utils/formatTime';
import { fIndianAmount } from '../../utils/formatNumber';
import { NOT_AVAILABLE } from '../Global/Constants';
import ReceivableFileToolbar from './ReceivableFileToolbar';
import ReceivableInvoiceToolbar from './ReceivableInvoiceToolbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import {
  downloadApiDataGet,
  getBankDropdownData,
  getLoanTypeData,
  getMasterListType,
  getStateData
} from '../../store/Slice/Global/GlobalSlice';
import { isValidValue } from '../../utils/CommonFunction';
import { getInvoiceList, getReceiveList } from '../../store/Slice/Receive/ReceiveSlice';
import useReceiveSelector from '../../store/Slice/Receive/ReceiveSelector';
import ReceivableDialogRedux from './ReceivableInvoicePaidDialog';
import useDebounce from '../../utils/useDebounce';
import { pillTabButtonSx } from '../Global/Styling';

// ----------------------------------------------------------------------

const TABLE_HEAD_FILES = [
  { id: 'bankName', label: 'BANK NAME', sortable: true },
  { id: 'fileNumber', label: 'FILE NO', sortable: true },
  { id: 'borrowerName', label: 'BORROWER NAME', sortable: true },
  { id: 'stateName', label: 'STATE NAME', sortable: true },
  { id: 'branchName', label: 'BRANCH NAME', sortable: true },
  { id: 'fileDisbursedDate', label: 'DISB DATE', sortable: true },
  { id: 'loanAmountDisbursed', label: 'DISB AMOUNT', sortable: true },
  { id: 'loanType', label: 'LOAN PRODUCT TYPE', sortable: false },
  { id: 'invoiceId', label: 'INVOICE NO', sortable: true },
  { id: 'misIsMatch', label: 'STATUS', sortable: true },
  { id: 'action', label: 'ACTION', sortable: false }
];

const TABLE_HEAD_INVOICE = [
  { id: 'invoiceNo', label: 'INVOICE NO', sortable: true },
  { id: 'invoiceDate', label: 'INVOICE DATE', sortable: true },
  { id: 'bankName', label: 'BANK', sortable: true },
  { id: 'branchName', label: 'BRANCH', sortable: true },
  { id: 'netAmount', label: 'NET AMOUNT', sortable: true },
  { id: 'totalGst', label: 'TOTAL GST', sortable: true },
  { id: 'partnerPayout', label: 'TOTAL AMOUNT', sortable: true },
  { id: 'partnerName', label: 'PARTNER NAME', sortable: true },
  { id: 'dueDate', label: 'DUE DATE', sortable: true },
  { id: 'duePastDues', label: 'PAST DUES', sortable: true },
  { id: 'statusText', label: 'STATUS', sortable: true },
  { id: 'action', label: 'ACTION', sortable: false }
];

const StyledHeadCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    backgroundColor: '#ffffff',
    color: '#1e40af',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap'
  }
}));

export default function Receivable() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('bankName');
  const [filterName, setFilterName] = useState('');
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filterParameter, setFilterParameter] = useState({
    loanTypeId: null,
    bankId: null,
    branchId: null,
    provinceId: null
  });

  const [invoiceFilterParameter, setInvoiceFilterParameter] = useState({
    bankName: null,
    branchName: null,
    status: null,
    fromDate: null,
    toDate: null,
    stateId: null
  });

  const [fileTypeId, setFileTypeId] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [openPayment, setOpenPayment] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [openTdsDialog, setOpenTdsDialog] = useState(false);
  const [selectedTdsRate, setSelectedTdsRate] = useState(4);
  const [tdsOnGstApplicable, setTdsOnGstApplicable] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleOpenReceiptFlow = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setOpenTdsDialog(true);
  };

  const handleTdsSelect = (isYes) => {
    const rate = isYes ? 4 : 2;
    setSelectedTdsRate(rate);
    setTdsOnGstApplicable(isYes);
    setOpenTdsDialog(false);
    setOpenPayment(true);
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { ReceiveListData = [], InvoiceListData = [] } = useReceiveSelector();
  const debouncedSearch = useDebounce(filterName, 600);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClickFiles = (event) => {
    if (event.target.checked) {
      const newSelecteds = ReceiveListData?.filter((n) => n.invoiceId === null) || [];
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleSelectAllClickInvoices = (event) => {
    if (event.target.checked) {
      const newSelecteds = InvoiceListData?.map((n) => n) || [];
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleInvoiceDownload = async (invoiceId) => {
    try {
      const result = await dispatch(
        downloadApiDataGet(`${ReceiveApi?.invoiceDownload}/${invoiceId}`, { invoiceId })
      );

      if (result?.success && result?.data) {
        const fileName = `Invoice_${invoiceId}_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}.pdf`;
        const url = window.URL.createObjectURL(result.data);
        setPreviewUrl(url);
        setOpenPreview(true);

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Download failed:', result?.error);
      }
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleClick = (event, row) => {
    const selectedIndex = selected.indexOf(row);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
    setSelected([]);
    setPage(0);
  };

  // ---------------- FILE LIST API ----------------
  const getReceiveListApi = useCallback(() => {
    if (!fileTypeId || !LoginData) return;

    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        sortOrder: order,
        pageNumber: page + 1
      },
      filterModel: {
        bankId: filterParameter?.bankId || null,
        branchId: filterParameter?.branchId || null,
        loanTypeId: filterParameter?.loanTypeId || null,
        stateId: filterParameter?.provinceId || null
      },
      revenueFileStatusId: fileTypeId
    };

    dispatch(getReceiveList(credentials));
  }, [LoginData, debouncedSearch, order, rowsPerPage, filterParameter, fileTypeId, dispatch, page]);

  // ---------------- INVOICE LIST API ----------------
  const getInvoiceListApi = useCallback(() => {
    if (!LoginData) return;

    const credentials = {
      userId: LoginData?.userId,
      companyId: LoginData?.companyId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: order
      },
      filterModel: {
        bankName: invoiceFilterParameter?.bankName || null,
        branchName: invoiceFilterParameter?.branchName || null,
        status: invoiceFilterParameter?.status || null,
        fromDate: invoiceFilterParameter?.fromDate || null,
        toDate: invoiceFilterParameter?.toDate || null,
        stateId: invoiceFilterParameter?.stateId || null
      }
    };

    dispatch(getInvoiceList(credentials));
  }, [LoginData, debouncedSearch, rowsPerPage, page, order, invoiceFilterParameter, dispatch]);

  useEffect(() => {
    if (tabValue === 0) {
      getReceiveListApi();
    } else {
      getInvoiceListApi();
    }
  }, [getReceiveListApi, getInvoiceListApi, tabValue]);

  useEffect(() => {
    const fetchMaster = async () => {
      const type = 'REVENUE_FILE_STATUS_TYPE';
      const result = await dispatch(getMasterListType(type));
      const masterTypes = result?.data?.masterTypes || [];
      if (masterTypes.length > 0) {
        setFileTypeId(masterTypes[0].typeId);
      }
    };
    fetchMaster();
    dispatch(getStateData({ countryId: 101 }));
  }, [dispatch]);

  const getLoanTypeBankDropAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(data));
    dispatch(getBankDropdownData(data));
  }, [LoginData, dispatch]);

  useEffect(() => {
    getLoanTypeBankDropAPi();
  }, [getLoanTypeBankDropAPi]);

  useEffect(() => {
    setPage(0);
  }, [filterName, tabValue, debouncedSearch]);

  const activeDataList = tabValue === 0 ? ReceiveListData : InvoiceListData;
  const currentTotal = activeDataList?.length || 0;
  const startRecord = currentTotal > 0 ? page * rowsPerPage + 1 : 0;
  const endRecord = currentTotal > 0 ? page * rowsPerPage + currentTotal : 0;

  return (
    <Page title="Partner Management System || Receivable">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Wallet Icon, Title, and Breadcrumbs */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3, gap: 2 }}
        >
          {/* Left Title with Wallet Icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Receivable
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                View and manage receivables from banks and partners.
              </Typography>
            </Box>
          </Stack>

          {/* Right Breadcrumbs */}
          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        {/* Top Bar with Tab Pills on Left and Search + Filter on Right */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2.5, gap: 2 }}
        >
          {/* Tabs: Files & Invoice */}
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Button
              onClick={() => handleTabChange(0)}
              startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={pillTabButtonSx(tabValue === 0)}
            >
              Files
            </Button>

            <Button
              onClick={() => handleTabChange(1)}
              startIcon={<ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={pillTabButtonSx(tabValue === 1)}
            >
              Invoice
            </Button>
          </Stack>

          {/* Search Box & Filter Button */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <OutlinedInput
              value={filterName}
              onChange={handleFilterByName}
              placeholder="Search Receivable..."
              startAdornment={
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              }
              sx={{
                width: { xs: '100%', sm: 260 },
                height: 40,
                borderRadius: '8px',
                bgcolor: '#ffffff',
                fontSize: 13.5,
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#0284c7' }
              }}
            />

            <Tooltip title="Filter">
              <IconButton
                onClick={() => setIsOpenFilter((prev) => !prev)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.08)' : '#ffffff',
                  color: isOpenFilter ? '#0284c7' : '#64748b',
                  '&:hover': { bgcolor: 'rgba(2, 132, 199, 0.12)' }
                }}
              >
                <FilterAltOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Main Content Card */}
        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          {/* ================= FILE TAB ================= */}
          {tabValue === 0 && (
            <>
              <ReceivableFileToolbar
                selected={selected}
                filterParameter={filterParameter}
                setFilterParameter={setFilterParameter}
                setSelected={setSelected}
                isOpenFilter={isOpenFilter}
                setIsOpenFilter={setIsOpenFilter}
              />

              <Scrollbar>
                <TableContainer sx={{ minWidth: 900, minHeight: 380 }}>
                  <Table>
                    <thead>
                      <TableRow>
                        <StyledHeadCell padding="checkbox" sx={{ pl: 2.5 }}>
                          <Checkbox
                            indeterminate={
                              selected.length > 0 &&
                              selected.length <
                                (ReceiveListData?.filter((n) => n.invoiceId === null).length || 0)
                            }
                            checked={
                              ReceiveListData?.filter((n) => n.invoiceId === null).length > 0 &&
                              selected.length ===
                                ReceiveListData?.filter((n) => n.invoiceId === null).length
                            }
                            onChange={handleSelectAllClickFiles}
                            sx={{
                              color: '#94a3b8',
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' },
                              '&.Mui-disabled': { color: '#e2e8f0' }
                            }}
                          />
                        </StyledHeadCell>
                        {TABLE_HEAD_FILES.map((headCell) => (
                          <StyledHeadCell
                            key={headCell.id}
                            onClick={() => headCell.sortable && handleRequestSort(headCell.id)}
                            sx={{
                              cursor: headCell.sortable ? 'pointer' : 'default',
                              userSelect: 'none',
                              ...(headCell.id === 'action' ? { textAlign: 'center' } : {})
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent={headCell.id === 'action' ? 'center' : 'flex-start'}
                              spacing={0.5}
                            >
                              <span>{headCell.label}</span>
                              {headCell.sortable && (
                                <SwapVertIcon
                                  sx={{
                                    fontSize: 16,
                                    color: orderBy === headCell.id ? '#0284c7' : '#94a3b8'
                                  }}
                                />
                              )}
                            </Stack>
                          </StyledHeadCell>
                        ))}
                      </TableRow>
                    </thead>
                    <TableBody>
                      {ReceiveListData?.length > 0 &&
                        ReceiveListData.map((item, index) => {
                          const isItemSelected = selected.indexOf(item) !== -1;
                          return (
                            <TableRow
                              hover
                              key={index}
                              tabIndex={-1}
                              role="checkbox"
                              selected={isItemSelected}
                              aria-checked={isItemSelected}
                              sx={{
                                '&:hover': { backgroundColor: '#f8fafc !important' },
                                '&.Mui-selected, &.Mui-selected:hover': {
                                  backgroundColor: 'rgba(2, 132, 199, 0.06)'
                                }
                              }}
                            >
                              <StyledTableCell padding="checkbox" sx={{ pl: 2.5 }}>
                                <Checkbox
                                  checked={isItemSelected}
                                  onChange={(event) => handleClick(event, item)}
                                  disabled={item?.invoiceId !== null}
                                  sx={{
                                    color: '#94a3b8',
                                    '&.Mui-checked': { color: '#0284c7' },
                                    '&.Mui-disabled': { color: '#e2e8f0' }
                                  }}
                                />
                              </StyledTableCell>

                              <StyledTableCell sx={{ color: '#334155' }}>
                                {item?.bankName || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#475569' }}>
                                {item?.fileNumber || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#334155' }}>
                                {item?.borrowerName || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#475569' }}>
                                {item?.stateName || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#475569' }}>
                                {item?.branchName || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#475569' }}>
                                {isValidValue(item?.fileDisbursedDate)
                                  ? fDate(item?.fileDisbursedDate)
                                  : NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#334155' }}>
                                {item?.loanAmountDisbursed
                                  ? fIndianAmount(item.loanAmountDisbursed)
                                  : NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#475569' }}>
                                {item?.loanType || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#475569' }}>
                                {item?.invoiceId || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell>
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 1.5,
                                    py: 0.35,
                                    borderRadius: '20px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    bgcolor:
                                      item?.misIsMatch === true
                                        ? '#dcfce7'
                                        : item?.misIsMatch === false
                                        ? '#fee2e2'
                                        : '#f1f5f9',
                                    color:
                                      item?.misIsMatch === true
                                        ? '#16a34a'
                                        : item?.misIsMatch === false
                                        ? '#dc2626'
                                        : '#64748b'
                                  }}
                                >
                                  {item?.misIsMatch === false
                                    ? 'Mis Matched'
                                    : item?.misIsMatch === true
                                    ? 'Matched'
                                    : NOT_AVAILABLE}
                                </Box>
                              </StyledTableCell>
                              <StyledTableCell align="center" width={80}>
                                <Tooltip title="View File" placement="top" arrow>
                                  <IconButton
                                    sx={{
                                      color: '#0284c7',
                                      backgroundColor: 'rgba(2, 132, 199, 0.08)',
                                      borderRadius: '8px',
                                      p: 0.75,
                                      '&:hover': { backgroundColor: 'rgba(2, 132, 199, 0.16)' }
                                    }}
                                    onClick={() => {
                                      const receivableIdData = {
                                        revenueId: item?.revenueId,
                                        leadId: item?.leadId,
                                        applicationId: item?.applicationId,
                                        recievableData: item
                                      };
                                      localStorage.setItem(
                                        'receivableIdData',
                                        JSON.stringify(receivableIdData)
                                      );
                                      navigate(`/receivable/view-file`, {
                                        state: {
                                          rowId: item
                                        }
                                      });
                                    }}
                                  >
                                    <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </StyledTableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                    {(!ReceiveListData || ReceiveListData.length === 0) && (
                      <TableBody>
                        <TableRow>
                          <StyledTableCell
                            align="center"
                            colSpan={TABLE_HEAD_FILES.length + 1}
                            sx={{ py: 5 }}
                          >
                            <SearchNotFound searchQuery={filterName} />
                          </StyledTableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Scrollbar>
            </>
          )}

          {/* ================= INVOICE TAB ================= */}
          {tabValue === 1 && (
            <>
              <ReceivableInvoiceToolbar
                setFilterParameter={setInvoiceFilterParameter}
                isOpenFilter={isOpenFilter}
                setIsOpenFilter={setIsOpenFilter}
              />

              <Scrollbar>
                <TableContainer sx={{ minWidth: 900, minHeight: 380 }}>
                  <Table>
                    <thead>
                      <TableRow>
                        <StyledHeadCell padding="checkbox" sx={{ pl: 2.5 }}>
                          <Checkbox
                            indeterminate={
                              selected.length > 0 &&
                              selected.length < (InvoiceListData?.length || 0)
                            }
                            checked={
                              InvoiceListData?.length > 0 &&
                              selected.length === InvoiceListData?.length
                            }
                            onChange={handleSelectAllClickInvoices}
                            sx={{
                              color: '#94a3b8',
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' },
                              '&.Mui-disabled': { color: '#e2e8f0' }
                            }}
                          />
                        </StyledHeadCell>
                        {TABLE_HEAD_INVOICE.map((headCell) => (
                          <StyledHeadCell
                            key={headCell.id}
                            onClick={() => headCell.sortable && handleRequestSort(headCell.id)}
                            sx={{
                              cursor: headCell.sortable ? 'pointer' : 'default',
                              userSelect: 'none',
                              ...(headCell.id === 'action' ? { textAlign: 'center' } : {})
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent={headCell.id === 'action' ? 'center' : 'flex-start'}
                              spacing={0.5}
                            >
                              <span>{headCell.label}</span>
                              {headCell.sortable && (
                                <SwapVertIcon
                                  sx={{
                                    fontSize: 16,
                                    color: orderBy === headCell.id ? '#0284c7' : '#94a3b8'
                                  }}
                                />
                              )}
                            </Stack>
                          </StyledHeadCell>
                        ))}
                      </TableRow>
                    </thead>
                    <TableBody>
                      {InvoiceListData?.length > 0 &&
                        InvoiceListData.map((row) => {
                          const isItemSelected = selected.indexOf(row) !== -1;
                          return (
                            <TableRow
                              hover
                              key={row.invoiceId}
                              tabIndex={-1}
                              role="checkbox"
                              selected={isItemSelected}
                              aria-checked={isItemSelected}
                              sx={{
                                '&:hover': { backgroundColor: '#f8fafc !important' },
                                '&.Mui-selected, &.Mui-selected:hover': {
                                  backgroundColor: 'rgba(2, 132, 199, 0.06)'
                                }
                              }}
                            >
                              <StyledTableCell padding="checkbox" sx={{ pl: 2.5 }}>
                                <Checkbox
                                  checked={isItemSelected}
                                  onChange={(event) => handleClick(event, row)}
                                  sx={{
                                    color: '#94a3b8',
                                    '&.Mui-checked': { color: '#0284c7' },
                                    '&.Mui-disabled': { color: '#e2e8f0' }
                                  }}
                                />
                              </StyledTableCell>

                              <StyledTableCell sx={{ color: '#0284c7', fontWeight: 600 }}>
                                {row?.invoiceNo || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#475569' }}>
                                {row?.invoiceDate ? fDate(row.invoiceDate) : NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#334155' }}>
                                {row?.bankName || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#475569' }}>
                                {row?.branchName || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#334155', fontWeight: 600 }}>
                                {fIndianAmount(row?.netAmount) ?? NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#475569' }}>
                                {fIndianAmount(row?.totalGst) ?? NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#0284c7', fontWeight: 700 }}>
                                {fIndianAmount(row?.partnerPayout) ?? NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#334155' }}>
                                {row?.partnerName || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#475569' }}>
                                {row?.dueDate ? fDate(row.dueDate) : NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell sx={{ color: '#475569' }}>
                                {row?.duePastDues || NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell>
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 1.5,
                                    py: 0.35,
                                    borderRadius: '20px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    bgcolor:
                                      row?.statusText === 'PAID'
                                        ? '#dcfce7'
                                        : row?.statusText === 'PENDING'
                                        ? '#fee2e2'
                                        : '#f1f5f9',
                                    color:
                                      row?.statusText === 'PAID'
                                        ? '#16a34a'
                                        : row?.statusText === 'PENDING'
                                        ? '#dc2626'
                                        : '#64748b'
                                  }}
                                >
                                  {row?.statusText
                                    ? sentenceCase(row.statusText.toLowerCase())
                                    : NOT_AVAILABLE}
                                </Box>
                              </StyledTableCell>
                              <StyledTableCell align="center" width={80}>
                                <UserMoreMenu
                                  rowId={row?.invoiceId}
                                  parent="receivable-invoice"
                                  statusCode={row?.statusText}
                                  onPayClick={() => {
                                    handleOpenReceiptFlow(row?.invoiceId);
                                  }}
                                  viewUserRow={handleInvoiceDownload}
                                />
                              </StyledTableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                    {(!InvoiceListData || InvoiceListData.length === 0) && (
                      <TableBody>
                        <TableRow>
                          <StyledTableCell
                            align="center"
                            colSpan={TABLE_HEAD_INVOICE.length + 1}
                            sx={{ py: 5 }}
                          >
                            <SearchNotFound searchQuery={filterName} />
                          </StyledTableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Scrollbar>
            </>
          )}

          {/* Pagination Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderTop: '1px solid #f1f5f9',
              gap: 2
            }}
          >
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>
              Showing{' '}
              <span style={{ fontWeight: 600, color: '#334155' }}>
                {startRecord} to {endRecord}
              </span>{' '}
              records
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>Rows per page</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    height: 32,
                    fontSize: 13,
                    borderRadius: '6px',
                    '& fieldset': { borderColor: '#e2e8f0' }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Stack>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  sx={{
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    width: 32,
                    height: 32,
                    color: '#64748b',
                    '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>

                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '6px',
                    bgcolor: '#0284c7',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 600,
                    px: 1
                  }}
                >
                  {page + 1}
                </Box>

                <IconButton
                  size="small"
                  disabled={currentTotal < rowsPerPage}
                  onClick={() => setPage((prev) => prev + 1)}
                  sx={{
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    width: 32,
                    height: 32,
                    color: '#64748b',
                    '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </Card>

        {/* TDS Confirmation Dialog */}
        <Dialog
          open={openTdsDialog}
          onClose={() => setOpenTdsDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2,
              px: 3,
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0'
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
              Is TDS applicable on GST?
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenTdsDialog(false)}
              sx={{ color: '#64748b' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3, pt: 2.5 }}>
            <Typography sx={{ fontSize: 14, color: '#475569', mb: 2, mt: 2 }}>
              Please select whether TDS is applicable on GST for this receipt:
            </Typography>
            <Box
              sx={{
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Typography sx={{ fontSize: 13, color: '#334155' }}>
                • <strong>Yes</strong>: Applies <strong>4%</strong> TDS deduction
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#334155' }}>
                • <strong>No</strong>: Applies <strong>2%</strong> TDS deduction
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              p: 2,
              px: 3,
              bgcolor: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              justifyContent: 'flex-end',
              gap: 1.5
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleTdsSelect(false)}
              sx={{
                minWidth: 80,
                borderRadius: '8px',
                fontWeight: 600,
                color: '#64748b',
                borderColor: '#cbd5e1',
                textTransform: 'none',
                '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
              }}
            >
              No
            </Button>
            <Button
              variant="contained"
              onClick={() => handleTdsSelect(true)}
              sx={{
                minWidth: 80,
                borderRadius: '8px',
                fontWeight: 600,
                bgcolor: '#13519d',
                textTransform: 'none',
                px: 3,
                '&:hover': { bgcolor: '#0d3d75' }
              }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment Dialog */}
        <ReceivableDialogRedux
          openPayment={openPayment}
          setOpenPayment={setOpenPayment}
          userId={LoginData?.userId}
          invoiceId={selectedInvoiceId}
          tdsRate={selectedTdsRate}
          tdsOnGstApplicable={tdsOnGstApplicable}
          onSuccess={() => {
            getInvoiceListApi();
          }}
        />

        {/* PDF Preview Modal */}
        <Dialog
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2,
              px: 3,
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0'
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
              Invoice Preview
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenPreview(false)}
              sx={{ color: '#64748b' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, minHeight: '70vh' }}>
            {previewUrl && (
              <iframe
                title="Invoice Preview"
                src={previewUrl}
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <Button
              variant="contained"
              onClick={() => setOpenPreview(false)}
              sx={{
                borderRadius: '8px',
                bgcolor: '#0284c7',
                '&:hover': { bgcolor: '#0369a1' }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
